import React, { useEffect, useMemo, useState } from "react";
import AppShell from "../../components/layout/AppShell";
import { api, formatApiError } from "../../lib/api";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import {
  Key, MagnifyingGlass, ShieldCheck, ShieldWarning, ArrowLeft,
  UserCircle, Phone, Envelope, Trash, Check, X,
} from "@phosphor-icons/react";

/**
 * Iter 145 · Phone CRM Passcodes
 *
 * Admin picks a Phone-CRM operator (roles: admin/manager/office/coordinator/hr)
 * and sets a 6-digit passcode. The Android companion app authenticates with
 *   POST /api/crm/phone/passcode/login  { identifier, passcode }
 * and gets a 30-day access JWT that works against /api/crm/phone/* endpoints.
 */

const ROLE_LABEL = {
  superadmin: "Super Admin",
  admin: "Admin",
  manager: "Manager",
  office: "Office",
  office_staff: "Office Staff",
  service_coordinator: "Coordinator",
  coordinator: "Coordinator",
  hr: "HR",
};

const ROLE_TONE = {
  superadmin: "bg-[#0B2B66] text-white",
  admin:       "bg-[#0B2B66] text-white",
  manager:     "bg-[#1E4E8C] text-white",
  office:      "bg-[#2D5A27] text-white",
  office_staff:"bg-[#2D5A27] text-white",
  service_coordinator: "bg-[#5A2E8C] text-white",
  coordinator: "bg-[#5A2E8C] text-white",
  hr:          "bg-[#7A5A11] text-white",
};

const fmt = (v) => {
  if (!v) return "—";
  try {
    return new Date(v).toLocaleString("en-IN", {
      day: "2-digit", month: "short", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return v; }
};

export default function PhonePasscodes() {
  const [staff, setStaff]   = useState([]);
  const [loading, setLoad]  = useState(true);
  const [q, setQ]           = useState("");
  const [tab, setTab]       = useState("all"); // all | with | without
  const [target, setTarget] = useState(null);   // staff row being edited
  const [showConfirm, setShowConfirm] = useState(null); // {row, action}

  const load = async () => {
    setLoad(true);
    try {
      const r = await api.get("/crm/phone/passcode/staff");
      setStaff(r.data?.staff || []);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoad(false); }
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return staff
      .filter((s) => {
        if (tab === "with"    && !s.has_passcode) return false;
        if (tab === "without" &&  s.has_passcode) return false;
        if (!needle) return true;
        return (
          (s.name  || "").toLowerCase().includes(needle) ||
          (s.email || "").toLowerCase().includes(needle) ||
          (s.phone || "").toLowerCase().includes(needle) ||
          (s.role  || "").toLowerCase().includes(needle)
        );
      });
  }, [staff, q, tab]);

  const counts = useMemo(() => ({
    total: staff.length,
    with: staff.filter((s) => s.has_passcode).length,
    without: staff.filter((s) => !s.has_passcode).length,
  }), [staff]);

  const doSet = async (row, passcode) => {
    try {
      await api.post("/crm/phone/passcode/set",
        { staff_id: row.id, passcode });
      toast.success(`Passcode saved for ${row.name}`);
      setTarget(null);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const doClear = async (row) => {
    try {
      await api.post("/crm/phone/passcode/clear", { staff_id: row.id });
      toast.success(`Passcode cleared for ${row.name}`);
      setShowConfirm(null);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <AppShell
      title="Phone CRM Passcodes"
      subtitle="Give each operator a 6-digit passcode to sign into the Rescigo Phone companion app"
      actions={
        <Link to="/crm/phone" data-testid="passcodes-back"
              className="h-10 px-3 border border-[#E5E2D9] rounded-md text-xs inline-flex items-center gap-1.5 hover:bg-[#F7F6F2]">
          <ArrowLeft size={12}/>Back to Phone CRM
        </Link>
      }
    >
      {/* Explainer strip */}
      <div className="bg-gradient-to-r from-[#0F1F0C] to-[#1B3B18] text-white rounded-lg p-5 mb-4"
           data-testid="passcodes-hero">
        <div className="flex items-start gap-4">
          <div className="w-11 h-11 rounded-full bg-white/10 grid place-items-center shrink-0">
            <Key size={22} weight="fill" className="text-[#D4E0D1]"/>
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-heading text-lg leading-tight">Companion-app sign-in</div>
            <div className="text-[13px] text-white/75 mt-1 max-w-2xl leading-relaxed">
              Operators open the Rescigo Phone app, enter their <b>phone or email</b> +
              this <b>6-digit passcode</b>, and immediately start syncing call logs.
              5 wrong attempts locks the account for 15 minutes.
            </div>
            <a href="https://github.com/rescigo/rescigo-master-panel/actions/workflows/companion-apk.yml"
                target="_blank" rel="noopener noreferrer"
                data-testid="passcodes-apk-download"
                className="mt-3 inline-flex items-center gap-1.5 h-9 px-3 rounded-md bg-white text-[#0F1F0C] text-xs font-bold hover:bg-white/90">
              <Key size={12} weight="bold"/>
              Download latest APK (GitHub Actions →)
            </a>
          </div>
        </div>
      </div>

      {/* Metric chips */}
      <div className="grid grid-cols-3 gap-2 mb-4" data-testid="passcodes-chips">
        <Chip label="Operators" value={counts.total} testid="chip-total"/>
        <Chip label="With passcode" value={counts.with} tone="green" testid="chip-with"/>
        <Chip label="Not set" value={counts.without} tone="amber" testid="chip-without"/>
      </div>

      {/* Filter + search */}
      <div className="flex items-center gap-2 flex-wrap mb-3">
        <div className="inline-flex bg-white border border-[#E5E2D9] rounded-md p-1"
             data-testid="passcodes-tabs">
          {[
            { v: "all",     l: "All",            n: counts.total  },
            { v: "with",    l: "With passcode",  n: counts.with    },
            { v: "without", l: "Not set",        n: counts.without },
          ].map((t) => (
            <button key={t.v} onClick={() => setTab(t.v)}
                    data-testid={`passcodes-tab-${t.v}`}
                    className={`h-8 px-3 rounded text-[11px] font-medium transition-colors ${
                      tab === t.v
                        ? "bg-[#2D5A27] text-white"
                        : "text-[#5C5A55] hover:bg-[#F7F6F2]"
                    }`}>
              {t.l} <span className="opacity-70 ml-1">({t.n})</span>
            </button>
          ))}
        </div>
        <div className="relative flex-1 min-w-[220px]">
          <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8A8780]"/>
          <input value={q} onChange={(e) => setQ(e.target.value)}
                 placeholder="Search name, phone, email, role…"
                 data-testid="passcodes-search"
                 className="w-full h-10 pl-9 pr-3 bg-white border border-[#E5E2D9] rounded-md text-sm focus:outline-none focus:border-[#2D5A27]"/>
        </div>
      </div>

      {/* Operator grid */}
      {loading && <div className="text-sm text-[#8A8780]">Loading operators…</div>}
      {!loading && filtered.length === 0 && (
        <div className="bg-white border border-[#E5E2D9] rounded-md p-8 text-center"
             data-testid="passcodes-empty">
          <UserCircle size={36} className="mx-auto text-[#8A8780]"/>
          <div className="font-heading text-lg mt-2">No operators match</div>
          <div className="text-sm text-[#5C5A55] mt-1">
            Try clearing the search or switching tabs.
          </div>
        </div>
      )}
      {!loading && filtered.length > 0 && (
        <div className="grid gap-2 md:grid-cols-2" data-testid="passcodes-list">
          {filtered.map((row) => (
            <OperatorCard
              key={row.id} row={row}
              onSet={() => setTarget(row)}
              onClear={() => setShowConfirm({ row, action: "clear" })}
            />
          ))}
        </div>
      )}

      {/* Set-passcode modal */}
      {target && (
        <PasscodeModal
          row={target}
          onCancel={() => setTarget(null)}
          onSave={(pc) => doSet(target, pc)}
        />
      )}

      {/* Confirm clear */}
      {showConfirm?.action === "clear" && (
        <ConfirmModal
          title={`Clear passcode for ${showConfirm.row.name}?`}
          message="The operator will no longer be able to sign into the companion app until you set a new passcode."
          confirmLabel="Clear passcode"
          onCancel={() => setShowConfirm(null)}
          onConfirm={() => doClear(showConfirm.row)}
        />
      )}
    </AppShell>
  );
}

// ── Operator card ─────────────────────────────────────────────────
function OperatorCard({ row, onSet, onClear }) {
  const hasPc = row.has_passcode;
  return (
    <div data-testid={`op-card-${row.id}`}
         className="bg-white border border-[#E5E2D9] rounded-md p-4 flex items-start gap-3">
      <div className={`w-11 h-11 rounded-full grid place-items-center shrink-0 ${
        hasPc ? "bg-[#D4E0D1] text-[#1A3317]" : "bg-[#F4E8DA] text-[#7A5A11]"
      }`}>
        {hasPc ? <ShieldCheck size={22} weight="fill"/> : <ShieldWarning size={22}/>}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <div className="font-heading text-[15px] font-medium truncate">{row.name}</div>
          <span className={`text-[10px] px-2 h-5 rounded-full inline-flex items-center font-medium ${
            ROLE_TONE[row.role] || "bg-[#EEEBE2] text-[#5C5A55]"
          }`}>{ROLE_LABEL[row.role] || row.role}</span>
        </div>
        <div className="text-[12px] text-[#5C5A55] mt-1 flex flex-wrap gap-x-3 gap-y-0.5">
          {row.phone && (
            <span className="inline-flex items-center gap-1" data-testid={`op-phone-${row.id}`}>
              <Phone size={11}/>{row.phone}
            </span>
          )}
          {row.email && (
            <span className="inline-flex items-center gap-1 truncate max-w-[220px]"
                  data-testid={`op-email-${row.id}`}>
              <Envelope size={11}/>{row.email}
            </span>
          )}
        </div>
        {hasPc && (
          <div className="mt-2 text-[11px] text-[#5C5A55]">
            Set {fmt(row.set_at)}
            {row.set_by_name && <span className="text-[#8A8780]"> by {row.set_by_name}</span>}
          </div>
        )}
        <div className="mt-3 flex gap-2 flex-wrap">
          <button onClick={onSet} data-testid={`op-set-${row.id}`}
                  className={`h-8 px-3 rounded-md text-[11px] font-medium inline-flex items-center gap-1.5 ${
                    hasPc
                      ? "bg-white border border-[#2D5A27] text-[#2D5A27] hover:bg-[#F7F6F2]"
                      : "bg-[#2D5A27] hover:bg-[#3A7033] text-white"
                  }`}>
            <Key size={11}/>{hasPc ? "Change passcode" : "Set passcode"}
          </button>
          {hasPc && (
            <button onClick={onClear} data-testid={`op-clear-${row.id}`}
                    className="h-8 px-3 rounded-md text-[11px] font-medium border border-[#E5E2D9] text-[#7A2E18] hover:bg-[#F7EFEC] inline-flex items-center gap-1.5">
              <Trash size={11}/>Clear
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Modal: set passcode ───────────────────────────────────────────
function PasscodeModal({ row, onCancel, onSave }) {
  const [p1, setP1] = useState("");
  const [p2, setP2] = useState("");
  const [reveal, setReveal] = useState(false);
  const err =
    p1 && !/^\d{6}$/.test(p1) ? "Passcode must be exactly 6 digits" :
    p2 && p1 !== p2 ? "Passcodes don't match" : "";
  const ok = /^\d{6}$/.test(p1) && p1 === p2 && !err;
  return (
    <ModalShell onCancel={onCancel} testid="passcode-modal">
      <div className="flex items-center gap-3 mb-4">
        <div className="w-10 h-10 rounded-md bg-[#2D5A27] text-white grid place-items-center">
          <Key size={20} weight="fill"/>
        </div>
        <div>
          <div className="font-heading text-lg leading-tight">
            {row.has_passcode ? "Change passcode" : "Set passcode"}
          </div>
          <div className="text-[12px] text-[#5C5A55]">
            for <b>{row.name}</b>{row.phone ? ` · ${row.phone}` : ""}
          </div>
        </div>
      </div>

      <label className="text-[11px] uppercase tracking-[0.14em] text-[#5C5A55]">
        6-digit passcode
      </label>
      <input value={p1} onChange={(e) => setP1(e.target.value.replace(/\D/g, "").slice(0, 6))}
             type={reveal ? "text" : "password"}
             inputMode="numeric" maxLength={6} autoFocus
             data-testid="passcode-input-1"
             className="w-full h-12 mt-1 px-3 border border-[#E5E2D9] rounded-md text-center text-[22px] font-mono tracking-[0.5em]"/>
      <label className="text-[11px] uppercase tracking-[0.14em] text-[#5C5A55] mt-3 block">
        Confirm passcode
      </label>
      <input value={p2} onChange={(e) => setP2(e.target.value.replace(/\D/g, "").slice(0, 6))}
             type={reveal ? "text" : "password"}
             inputMode="numeric" maxLength={6}
             data-testid="passcode-input-2"
             className="w-full h-12 mt-1 px-3 border border-[#E5E2D9] rounded-md text-center text-[22px] font-mono tracking-[0.5em]"/>

      <label className="flex items-center gap-2 mt-3 text-[12px] text-[#5C5A55]">
        <input type="checkbox" checked={reveal} onChange={(e) => setReveal(e.target.checked)}
               data-testid="passcode-reveal"/>
        Show passcode
      </label>

      {err && <div className="text-[12px] text-[#7A2E18] mt-2" data-testid="passcode-err">{err}</div>}

      <div className="text-[11px] text-[#5C5A55] mt-4 bg-[#F7F6F2] rounded-md p-3">
        Share this passcode with the operator securely (SMS / WhatsApp). They&apos;ll enter it
        along with their <b>phone or email</b> in the Rescigo Phone app.
      </div>

      <div className="flex justify-end gap-2 mt-5">
        <button onClick={onCancel} data-testid="passcode-cancel"
                className="h-10 px-4 border border-[#E5E2D9] rounded-md text-sm hover:bg-[#F7F6F2]">
          Cancel
        </button>
        <button onClick={() => ok && onSave(p1)} disabled={!ok}
                data-testid="passcode-save"
                className="h-10 px-4 bg-[#2D5A27] hover:bg-[#3A7033] disabled:opacity-40 text-white rounded-md text-sm font-medium inline-flex items-center gap-1.5">
          <Check size={13} weight="bold"/>Save passcode
        </button>
      </div>
    </ModalShell>
  );
}

// ── Modal: confirm ────────────────────────────────────────────────
function ConfirmModal({ title, message, confirmLabel, onCancel, onConfirm }) {
  return (
    <ModalShell onCancel={onCancel} testid="confirm-modal">
      <div className="font-heading text-lg mb-2">{title}</div>
      <div className="text-sm text-[#5C5A55] leading-relaxed">{message}</div>
      <div className="flex justify-end gap-2 mt-5">
        <button onClick={onCancel} data-testid="confirm-cancel"
                className="h-10 px-4 border border-[#E5E2D9] rounded-md text-sm hover:bg-[#F7F6F2]">
          Cancel
        </button>
        <button onClick={onConfirm} data-testid="confirm-yes"
                className="h-10 px-4 bg-[#7A2E18] hover:bg-[#95371C] text-white rounded-md text-sm font-medium inline-flex items-center gap-1.5">
          <Trash size={13} weight="bold"/>{confirmLabel}
        </button>
      </div>
    </ModalShell>
  );
}

// ── Chrome ────────────────────────────────────────────────────────
function ModalShell({ onCancel, testid, children }) {
  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4"
         onClick={onCancel} data-testid={testid}>
      <div className="bg-white rounded-lg shadow-lg p-5 w-full max-w-md relative"
           onClick={(e) => e.stopPropagation()}>
        <button onClick={onCancel} aria-label="Close"
                className="absolute top-3 right-3 w-8 h-8 rounded-md hover:bg-[#F7F6F2] grid place-items-center">
          <X size={14}/>
        </button>
        {children}
      </div>
    </div>
  );
}

function Chip({ label, value, tone, testid }) {
  const tones = {
    green: "bg-[#D4E0D1] text-[#1A3317] border-[#B5CBAF]",
    amber: "bg-[#F4E8DA] text-[#7A5A11] border-[#E4D3B2]",
  };
  const cls = tones[tone] || "bg-white text-[#0F1F0C] border-[#E5E2D9]";
  return (
    <div data-testid={testid} className={`border rounded-md p-3 ${cls}`}>
      <div className="text-[10px] uppercase tracking-[0.14em] opacity-70">{label}</div>
      <div className="text-xl font-heading font-semibold mt-0.5">{value}</div>
    </div>
  );
}
